import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  FaSearch,
  FaPlus,
  FaClock,
  FaTh,
  FaCog,
  FaChevronDown,
} from 'react-icons/fa';
import GlobalSearchApi from '../../api/GlobalSearchApi';
import EditLayoutModal from '../layout/EditLayoutModal';
import { displayRecentTitle, filterRecentlyViewed } from '../../utils/recentlyViewed';
import { canAccessSetup } from '../../utils/entitlements';
import {
  getSearchFieldDisplayText,
  highlightText,
  initials,
  objectTone,
} from './globalSearchUi';
import './GlobalSearch.css';

const SUGGESTION_LIMIT = 10;
const PER_OBJECT_ALL = 3;
const PER_OBJECT_SCOPED = 8;

export default function GlobalSearchPalette({
  open,
  query,
  onQueryChange,
  onClose,
  apps = [],
  setupItems = [],
  inputRef,
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [allowedObjects, setAllowedObjects] = useState([]);
  const [objectScope, setObjectScope] = useState('');
  const [objectFilter, setObjectFilter] = useState('');
  const [objectMenuOpen, setObjectMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [createObjectName, setCreateObjectName] = useState(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const listRef = useRef(null);
  const objectInputRef = useRef(null);
  const objectScopeRef = useRef(objectScope);
  objectScopeRef.current = objectScope;

  const allowedNames = useMemo(
    () => allowedObjects.map((o) => o.objectName),
    [allowedObjects]
  );

  const scopedAllowedNames = useMemo(() => {
    if (!objectScope) return allowedNames;
    return allowedNames.filter(
      (n) => String(n).toLowerCase() === String(objectScope).toLowerCase()
    );
  }, [allowedNames, objectScope]);

  const recents = useMemo(
    () => filterRecentlyViewed(scopedAllowedNames),
    [scopedAllowedNames, open]
  );

  const q = String(query || '').trim();
  const qLower = q.toLowerCase();
  const typing = q.length >= 2;

  const selectedObjectMeta = useMemo(
    () =>
      allowedObjects.find(
        (o) =>
          String(o.objectName).toLowerCase() === String(objectScope).toLowerCase()
      ) || null,
    [allowedObjects, objectScope]
  );

  const scopeOptions = useMemo(() => {
    return [
      { key: '', label: 'All objects' },
      ...allowedObjects.map((o) => ({
        key: o.objectName,
        label: o.pluralLabel || o.objectLabel || o.objectName,
      })),
    ];
  }, [allowedObjects]);

  const filteredObjectOptions = useMemo(() => {
    const filter = objectFilter.trim().toLowerCase();
    if (!filter) return scopeOptions;
    return scopeOptions.filter((o) => {
      const label = String(o.label || '').toLowerCase();
      const key = String(o.key || '').toLowerCase();
      return label.includes(filter) || key.includes(filter);
    });
  }, [scopeOptions, objectFilter]);

  const filteredApps = useMemo(() => {
    if (objectScope) return [];
    if (!qLower) return (apps || []).slice(0, 5);
    return (apps || [])
      .filter((a) => String(a.name || '').toLowerCase().includes(qLower))
      .slice(0, 5);
  }, [apps, qLower, objectScope]);

  const filteredSetup = useMemo(() => {
    if (objectScope) return [];
    if (!canAccessSetup()) return [];
    const items = setupItems || [];
    if (!qLower) return items.slice(0, 4);
    return items
      .filter(
        (s) =>
          String(s.label || s.id || '')
            .toLowerCase()
            .includes(qLower)
      )
      .slice(0, 5);
  }, [setupItems, qLower, objectScope]);

  const newActions = useMemo(() => {
    let list = (allowedObjects || []).filter((o) => o.canCreate);
    if (objectScope) {
      list = list.filter(
        (o) =>
          String(o.objectName).toLowerCase() === String(objectScope).toLowerCase()
      );
    }
    if (!qLower) return list.slice(0, 4);
    return list
      .filter((o) => {
        const hay = `${o.objectLabel} ${o.pluralLabel} new`.toLowerCase();
        return hay.includes(qLower) || qLower.startsWith('new');
      })
      .slice(0, 5);
  }, [allowedObjects, qLower, objectScope]);

  const viewAllHref = useMemo(() => {
    const params = new URLSearchParams();
    params.set('q', q);
    if (objectScope) params.set('object', objectScope);
    return `/search?${params.toString()}`;
  }, [q, objectScope]);

  const totalResultCount = useMemo(
    () => groups.reduce((n, g) => n + (g.records?.length || 0), 0),
    [groups]
  );

  /** Flat list for keyboard nav + grouped sections for rendering */
  const { flatItems, sections } = useMemo(() => {
    const items = [];
    const secs = [];

    const pushSection = (title, sectionItems) => {
      if (!sectionItems.length) return;
      secs.push({ title, items: sectionItems });
      items.push(...sectionItems);
    };

    if (!typing) {
      pushSection(
        'Recently viewed',
        recents.slice(0, 6).map((r) => ({
          key: `recent:${r.objectName}:${r.id}`,
          kind: 'recent',
          label: displayRecentTitle(r),
          hint: r.objectLabel,
          objectName: r.objectName,
          href: r.href,
          goLabel: 'Open ↵',
        }))
      );
      pushSection(
        'Apps',
        filteredApps.map((a) => ({
          key: `app:${a.id || a.name}`,
          kind: 'app',
          label: a.name,
          hint: 'App',
          href: a.id ? `/?app=${a.id}` : '/',
          app: a,
          goLabel: 'Open ↵',
        }))
      );
      pushSection(
        'Quick actions',
        [
          ...newActions.map((o) => ({
            key: `new:${o.objectName}`,
            kind: 'action',
            label: `New ${o.objectLabel}`,
            hint: 'Create',
            objectName: o.objectName,
            goLabel: 'Create ↵',
          })),
          ...filteredSetup.map((s) => ({
            key: `setup:${s.id}`,
            kind: 'setup',
            label: s.label || s.id,
            hint: 'Setup',
            href: s.path,
            goLabel: 'Open ↵',
          })),
        ].slice(0, 6)
      );
      return { flatItems: items, sections: secs };
    }

    const perObject = objectScope ? PER_OBJECT_SCOPED : PER_OBJECT_ALL;
    let remaining = SUGGESTION_LIMIT;
    for (const g of groups) {
      if (remaining <= 0) break;
      const slice = (g.records || []).slice(0, Math.min(perObject, remaining));
      const sectionItems = slice.map((rec) => {
        const cols = g.columns || [];
        const titleCol = cols[0];
        const titleField = titleCol?.fieldName;
        const title = titleField
          ? getSearchFieldDisplayText(
              rec.values?.[titleField],
              titleCol?.dataType,
              titleField
            )
          : '';
        const extraParts = [];
        if (g.objectLabel) extraParts.push(g.objectLabel);
        for (let i = 1; i < Math.min(cols.length, 3); i += 1) {
          const col = cols[i];
          const v = getSearchFieldDisplayText(
            rec.values?.[col.fieldName],
            col?.dataType,
            col?.fieldName
          );
          if (v) extraParts.push(v);
        }
        return {
          key: `rec:${g.objectName}:${rec.id}`,
          kind: 'record',
          label: title || rec.id,
          hint: extraParts.join(' · '),
          objectName: g.objectName,
          objectLabel: g.objectLabel,
          href: rec.href,
          goLabel: 'Open ↵',
        };
      });
      pushSection(g.pluralLabel || g.objectLabel || g.objectName, sectionItems);
      remaining -= sectionItems.length;
    }

    if (!objectScope) {
      pushSection(
        'Actions & setup',
        [
          ...newActions.map((o) => ({
            key: `new:${o.objectName}`,
            kind: 'action',
            label: `New ${o.objectLabel}`,
            hint: 'Create',
            objectName: o.objectName,
            goLabel: 'Create ↵',
          })),
          ...filteredApps.map((a) => ({
            key: `app:${a.id || a.name}`,
            kind: 'app',
            label: a.name,
            hint: 'App',
            href: a.id ? `/?app=${a.id}` : '/',
            app: a,
            goLabel: 'Open ↵',
          })),
          ...filteredSetup.map((s) => ({
            key: `setup:${s.id}`,
            kind: 'setup',
            label: s.label || s.id,
            hint: 'Setup',
            href: s.path,
            goLabel: 'Open ↵',
          })),
        ].slice(0, 5)
      );
    }

    return { flatItems: items, sections: secs };
  }, [
    typing,
    recents,
    filteredApps,
    filteredSetup,
    newActions,
    groups,
    objectScope,
  ]);

  const runSearch = useCallback((term, scope) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const api = new GlobalSearchApi();
    const trimmed = String(term || '').trim();
    setLoading(trimmed.length >= 2);
    api
      .search({
        q: term,
        limit: SUGGESTION_LIMIT,
        objects: scope || undefined,
        signal: controller.signal,
      })
      .then((res) => {
        const data = res?.data?.data || {};
        setGroups(data.groups || []);
        setAllowedObjects(data.allowedObjects || []);
      })
      .catch((err) => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        console.warn('Global search failed', err);
      })
      .finally(() => {
        if (abortRef.current === controller) setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!open) return;
    setObjectScope('');
    setObjectFilter('');
    setObjectMenuOpen(false);
    runSearch(q.length >= 2 ? q : '', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, runSearch]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(query, objectScopeRef.current);
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, runSearch]);

  useEffect(() => {
    if (!open) return;
    runSearch(query, objectScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectScope]);

  useEffect(() => {
    setActiveIndex(0);
  }, [flatItems.length, query, objectScope]);

  const selectObject = useCallback(
    (objectName) => {
      setObjectScope(objectName || '');
      setObjectFilter('');
      setObjectMenuOpen(false);
      setTimeout(() => inputRef?.current?.focus(), 0);
    },
    [inputRef]
  );

  const cycleScope = useCallback(
    (dir) => {
      if (scopeOptions.length === 0) return;
      const keys = scopeOptions.map((c) => c.key);
      const idx = keys.indexOf(objectScope);
      const next = (idx + dir + keys.length) % keys.length;
      selectObject(keys[next]);
    },
    [scopeOptions, objectScope, selectObject]
  );

  const closeCreateModal = useCallback(() => {
    setCreateObjectName(null);
  }, []);

  const go = useCallback(
    (item) => {
      if (item?.kind === 'action' && item.objectName) {
        onClose();
        onQueryChange('');
        setObjectScope('');
        setCreateObjectName(item.objectName);
        return;
      }
      if (!item?.href) return;
      onClose();
      onQueryChange('');
      setObjectScope('');
      if (item.kind === 'app' && item.app?.id) {
        try {
          localStorage.setItem('vgrow_selected_app_id', String(item.app.id));
        } catch {
          /* ignore */
        }
      }
      navigate(item.href);
    },
    [navigate, onClose, onQueryChange]
  );

  const seeAll = useCallback(() => {
    if (!q) return;
    onClose();
    onQueryChange('');
    setObjectScope('');
    navigate(viewAllHref);
  }, [q, onClose, onQueryChange, navigate, viewAllHref]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (objectMenuOpen) {
        if (e.key === 'Escape') {
          e.preventDefault();
          setObjectMenuOpen(false);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        cycleScope(e.shiftKey ? -1 : 1);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(flatItems.length - 1, 0)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (e.shiftKey || flatItems.length === 0) {
          seeAll();
          return;
        }
        const item = flatItems[activeIndex];
        if (item) go(item);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    open,
    flatItems,
    activeIndex,
    go,
    onClose,
    cycleScope,
    seeAll,
    objectMenuOpen,
  ]);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const indexByKey = useMemo(() => {
    const map = new Map();
    flatItems.forEach((item, i) => map.set(item.key, i));
    return map;
  }, [flatItems]);

  const createModal =
    createObjectName != null ? (
      <EditLayoutModal
        show
        onHide={closeCreateModal}
        objectName={createObjectName}
        sourcePage="listview"
        onSave={closeCreateModal}
        onCancel={closeCreateModal}
      />
    ) : null;

  if (!open) return createModal;

  const avatarFor = (item) => {
    if (item.kind === 'recent' || item.kind === 'record') {
      const tone = objectTone(item.objectName);
      return (
        <span className={`gs-av t-${tone}`} aria-hidden="true">
          {initials(item.label)}
        </span>
      );
    }
    if (item.kind === 'app') {
      return (
        <span className="gs-av t-muted is-square" aria-hidden="true">
          <FaTh size={12} />
        </span>
      );
    }
    if (item.kind === 'setup') {
      return (
        <span className="gs-av t-muted is-square" aria-hidden="true">
          <FaCog size={12} />
        </span>
      );
    }
    if (item.kind === 'action') {
      return (
        <span className="gs-av t-muted is-square" aria-hidden="true">
          <FaPlus size={12} />
        </span>
      );
    }
    return (
      <span className="gs-av t-muted is-square" aria-hidden="true">
        <FaClock size={12} />
      </span>
    );
  };

  const objectDisplayLabel = selectedObjectMeta
    ? selectedObjectMeta.pluralLabel || selectedObjectMeta.objectLabel
    : 'All objects';

  const placeholder = selectedObjectMeta
    ? `Search ${selectedObjectMeta.pluralLabel || selectedObjectMeta.objectLabel}…`
    : 'Search records, apps and actions';

  return (
    <>
      {createModal}
      {createPortal(
    <div className="gs-overlay" onMouseDown={onClose}>
      <div
        className="gs-palette"
        role="dialog"
        aria-label="Global search"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="gs-palette-pin">
          <div className={`gs-object-combo${objectMenuOpen ? ' is-open' : ''}`}>
            <button
              type="button"
              className="gs-object-combo-toggle"
              aria-expanded={objectMenuOpen}
              aria-haspopup="listbox"
              aria-label="Search in object"
              onClick={() => {
                setObjectMenuOpen((v) => !v);
                setTimeout(() => objectInputRef.current?.focus(), 0);
              }}
            >
              <span className="gs-object-combo-label text-truncate">
                {objectDisplayLabel}
              </span>
              <FaChevronDown size={10} aria-hidden="true" />
            </button>
            {objectMenuOpen ? (
              <div className="gs-object-combo-menu" role="listbox">
                <input
                  ref={objectInputRef}
                  type="search"
                  className="gs-object-combo-filter"
                  value={objectFilter}
                  onChange={(e) => setObjectFilter(e.target.value)}
                  placeholder="Type object name…"
                  aria-label="Filter objects"
                  autoComplete="off"
                />
                {filteredObjectOptions.length === 0 ? (
                  <div className="gs-object-combo-empty">No matching objects</div>
                ) : (
                  filteredObjectOptions.map((o) => (
                    <button
                      key={o.key || 'all'}
                      type="button"
                      role="option"
                      className={`gs-object-combo-option${
                        objectScope === o.key ? ' is-selected' : ''
                      }`}
                      onClick={() => selectObject(o.key)}
                    >
                      {o.label}
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>
          <FaSearch className="gs-palette-pin-icon" size={16} aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => {
              setObjectMenuOpen(false);
              onQueryChange(e.target.value);
            }}
            onFocus={() => setObjectMenuOpen(false)}
            placeholder={placeholder}
            aria-label="Global search"
            autoComplete="off"
          />
          <kbd className="gs-palette-esc">Esc</kbd>
        </div>

        <div className="gs-palette-list" ref={listRef} role="listbox">
          {loading && typing ? (
            <div className="gs-palette-empty">Searching…</div>
          ) : null}

          {!loading && flatItems.length === 0 ? (
            <div className="gs-palette-empty">
              <strong>
                {typing
                  ? selectedObjectMeta
                    ? `Nothing found for “${q}”`
                    : `Nothing found for “${q}”`
                  : 'Start typing to search'}
              </strong>
              {typing
                ? 'Try fewer words, or switch to All objects.'
                : 'Recently viewed records and actions appear here.'}
            </div>
          ) : null}

          {!loading &&
            sections.map((sec) => (
              <div key={sec.title}>
                <div className="gs-palette-grp">{sec.title}</div>
                {sec.items.map((item) => {
                  const index = indexByKey.get(item.key) ?? 0;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      role="option"
                      aria-selected={index === activeIndex}
                      data-active={index === activeIndex ? 'true' : undefined}
                      className={`gs-palette-row${
                        index === activeIndex ? ' is-active' : ''
                      }`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => go(item)}
                    >
                      {avatarFor(item)}
                      <span className="gs-palette-body">
                        <span className="gs-palette-title">
                          {highlightText(item.label, query)}
                        </span>
                        {item.hint ? (
                          <span className="gs-palette-hint">
                            {highlightText(item.hint, query)}
                          </span>
                        ) : null}
                      </span>
                      <span className="gs-palette-go">{item.goLabel || 'Open ↵'}</span>
                    </button>
                  );
                })}
              </div>
            ))}
        </div>

        <div className="gs-palette-footer">
          <span>
            <kbd className="gs-kbd">↑</kbd> <kbd className="gs-kbd">↓</kbd> move
          </span>
          <span>
            <kbd className="gs-kbd">↵</kbd> open
          </span>
          <span>
            <kbd className="gs-kbd">Tab</kbd> switch object
          </span>
          <span className="gs-palette-footer-sp" />
          {typing ? (
            <button type="button" className="gs-palette-seeall" onClick={seeAll}>
              See all {totalResultCount} results
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
      )}
    </>
  );
}
